import { useEffect, useId, useRef, type ReactNode } from 'react'
import { motion, useAnimation, useMotionValue, useReducedMotion } from 'framer-motion'
import { measurePromptBounds } from '@/lib/promptBounds'
import { dvdPositionAt } from '@/lib/dvdMotion'
import type { ScreenDesign, StartPromptSettings } from '@/types'

export const START_PROMPT_DEFAULTS: StartPromptSettings = {
  mode: 'builtin', variant: 'camera', accent: '#2dd4bf', secondary: '#a78bfa',
  label: 'Tap anywhere to start', size: 340, roaming: true, movement: 'roam', speed: 1,
  backgroundEnabled: true, backgroundOpacity: 1, artworkOpacity: 1, textOpacity: 1,
}

export function resolveStartPrompt(screen: ScreenDesign): StartPromptSettings {
  const value = { ...START_PROMPT_DEFAULTS, ...screen.startPrompt }
  const opacity = (n: number) => Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 1
  return { ...value, movement: screen.startPrompt?.movement ?? (value.roaming ? 'roam' : 'still'), backgroundOpacity: opacity(value.backgroundOpacity), artworkOpacity: opacity(value.artworkOpacity), textOpacity: opacity(value.textOpacity), size: Number.isFinite(value.size) ? Math.max(200, Math.min(600, value.size)) : 340, speed: Number.isFinite(value.speed) ? Math.max(.1, Math.min(20, value.speed)) : 1 }
}

export function StartArtwork({ variant, accent, secondary }: Pick<StartPromptSettings, 'variant' | 'accent' | 'secondary'>) {
  const id = useId().replace(/:/g, '')
  return <svg viewBox="0 0 280 210" aria-hidden="true" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
    <defs>
      <linearGradient id={`${id}body`} x2="0.8" y2="1"><stop stopColor="#f8fafc" /><stop offset=".45" stopColor="#94a3b8" /><stop offset="1" stopColor="#334155" /></linearGradient>
      <linearGradient id={`${id}accent`} x2="1" y2="1"><stop stopColor={accent} /><stop offset="1" stopColor={secondary} /></linearGradient>
      <radialGradient id={`${id}lens`} cx="35%" cy="30%"><stop stopColor={secondary} /><stop offset=".45" stopColor="#172554" /><stop offset="1" stopColor="#020617" /></radialGradient>
    </defs>
    <ellipse cx="140" cy="188" rx="88" ry="10" fill="#000" opacity=".35" />
    {variant === 'camera' ? <g strokeLinejoin="round">
      <path d="M44 58h42l13-22h61l15 22h58q15 0 15 16v89q0 16-15 16H44q-15 0-15-16V74q0-16 15-16Z" fill={`url(#${id}body)`} stroke="#e2e8f0" strokeWidth="2" />
      <path d="M30 98h217v53H30Z" fill="#0f172a" />
      <rect x="48" y="44" width="28" height="14" rx="5" fill={accent} />
      <rect x="202" y="71" width="27" height="13" rx="5" fill={accent} />
      <circle cx="139" cy="117" r="65" fill="#0f172a" stroke={`url(#${id}accent)`} strokeWidth="7" />
      <circle cx="139" cy="117" r="51" fill={`url(#${id}lens)`} stroke="#64748b" strokeWidth="3" />
      <circle cx="139" cy="117" r="33" fill="none" stroke={accent} strokeWidth="2" opacity=".7" />
      <ellipse cx="122" cy="97" rx="17" ry="10" fill="#fff" opacity=".5" transform="rotate(-35 122 97)" />
    </g> : variant === 'snapshot' ? <g>
      <rect x="64" y="29" width="153" height="161" rx="14" transform="rotate(12 140 110)" fill={secondary} stroke="#fff" strokeWidth="2" />
      <g transform="rotate(-9 140 110)"><rect x="51" y="19" width="161" height="174" rx="14" fill="#f8fafc" /><rect x="63" y="31" width="137" height="119" rx="7" fill="#0f172a" /><circle cx="164" cy="62" r="15" fill={secondary} /><path d="m63 133 41-54 31 37 23-24 42 41v17H63Z" fill={`url(#${id}accent)`} /><rect x="102" y="165" width="61" height="5" rx="2.5" fill="#64748b" /></g>
    </g> : <g>
      <circle cx="140" cy="104" r="83" fill="#0f172a" stroke={`url(#${id}accent)`} strokeWidth="6" />
      {Array.from({ length: 6 }, (_, i) => <path key={i} d="m140 35 45 26-25 43h-40Z" transform={`rotate(${i * 60} 140 104)`} fill={i % 2 ? secondary : accent} stroke="#0f172a" strokeWidth="3" />)}
      <circle cx="140" cy="104" r="25" fill={`url(#${id}lens)`} />
    </g>}
    <path d="m242 21 3 10 10 3-10 3-3 10-3-10-10-3 10-3Z" fill={accent} />
    <circle cx="28" cy="38" r="4" fill={secondary} />
  </svg>
}

function DvdMotion({ width, height, size, speed, children }: {
  width: number; height: number; size: number; speed: number; children: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  useEffect(() => {
    const element = ref.current
    if (!element) return
    let frame = 0
    const restart = () => {
      cancelAnimationFrame(frame)
      const bounds = measurePromptBounds(element, size)
      const maxX = Math.max(0, width - (bounds.right - bounds.left))
      const maxY = Math.max(0, height - (bounds.bottom - bounds.top))
      let elapsed = 0
      let previous: number | undefined
      const tick = (timestamp: number) => {
        // Pause during background-tab suspension instead of jumping on return.
        if (previous !== undefined) elapsed += Math.min((timestamp - previous) / 1000, .1)
        previous = timestamp
        const position = dvdPositionAt(elapsed, maxX, maxY, speed)
        x.set(position.x - bounds.left)
        y.set(position.y - bounds.top)
        frame = requestAnimationFrame(tick)
      }
      const position = dvdPositionAt(0, maxX, maxY, speed)
      x.set(position.x - bounds.left)
      y.set(position.y - bounds.top)
      frame = requestAnimationFrame(tick)
    }
    const observer = new ResizeObserver(restart)
    const changes = new MutationObserver(records => {
      // Motion updates the outer transform every frame; only content changes affect bounds.
      if (records.some(record => record.type !== 'attributes' || record.target !== element)) restart()
    })
    observer.observe(element)
    changes.observe(element, { childList: true, subtree: true, attributes: true, characterData: true, attributeFilter: ['style', 'src'] })
    element.addEventListener('load', restart, true)
    return () => { observer.disconnect(); changes.disconnect(); element.removeEventListener('load', restart, true); cancelAnimationFrame(frame) }
  }, [width, height, speed, size, x, y])
  return <motion.div ref={ref} style={{ x, y, position: 'absolute', left: 0, top: 0, width: size, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{children}</motion.div>
}

function PromptMotion({ movement, speed, travelX, travelY, size, canvasWidth, canvasHeight, children }: {
  movement: StartPromptSettings['movement']; speed: number; travelX: number; travelY: number; size: number; canvasWidth: number; canvasHeight: number; children: ReactNode
}) {
  const controls = useAnimation()
  useEffect(() => {
    let cancelled = false
    controls.set({ x: 0, y: 0, opacity: 1, scale: 1 })
    if (movement === 'random') {
      // Change position only while invisible; each appearance gets a fresh location.
      const appear = async () => {
        while (!cancelled) {
          controls.set({ x: (Math.random() * 2 - 1) * travelX, y: (Math.random() * 2 - 1) * travelY, opacity: 0 })
          await controls.start({ opacity: [0, 1, 1, 0, 0], transition: { duration: 7 / speed, times: [0, .12, .8, .92, 1], ease: 'easeInOut' } })
        }
      }
      void appear()
    } else if (movement === 'roam') {
      void controls.start({ x: [0, travelX, -travelX, 0], y: [0, -travelY, travelY, 0], transition: { x: { duration: 48 / speed, repeat: Infinity, ease: 'linear' }, y: { duration: 37 / speed, repeat: Infinity, ease: 'easeInOut' } } })
    } else if (movement === 'float') {
      void controls.start({ y: [0, -Math.min(28, travelY), 0, Math.min(28, travelY), 0], transition: { duration: 8 / speed, repeat: Infinity, ease: 'easeInOut' } })
    } else if (movement === 'pulse') {
      void controls.start({ scale: [1, .94, 1], transition: { duration: 4 / speed, repeat: Infinity, ease: 'easeInOut' } })
    }
    return () => { cancelled = true; controls.stop() }
  }, [controls, movement, speed, travelX, travelY])
  if (movement === 'dvd') return <DvdMotion width={canvasWidth} height={canvasHeight} size={size} speed={speed}>{children}</DvdMotion>
  return <motion.div animate={controls} style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{children}</motion.div>
}

export default function StartPrompt({ screen }: { screen: ScreenDesign }) {
  const settings = resolveStartPrompt(screen)
  const reducedMotion = useReducedMotion()
  if (settings.mode === 'off') return null
  const custom = settings.mode === 'custom'
  if (custom && !screen.startButtonImage?.asset) return null
  const width = screen.width || 1920
  const height = screen.height || 1080
  const size = Math.min(Math.max(settings.size, 200), width * .65, height * .65)
  // Keep the entire badge inside the canvas, with an additional edge margin.
  const travelX = Math.max(0, (width - size) / 2 - width * .06)
  const travelY = Math.max(0, (height - size) / 2 - height * .08)
  const movement = reducedMotion ? 'still' : settings.movement
  return <div className="absolute inset-0 pointer-events-none flex items-center justify-center" style={{ zIndex: 100 }}>
    <PromptMotion movement={movement} speed={settings.speed} travelX={travelX} travelY={travelY} size={size} canvasWidth={width} canvasHeight={height}>
      {custom ? <img data-prompt-image src={screen.startButtonImage!.asset!} alt={settings.label} style={{ width: screen.startButtonImage?.width || 'auto', height: screen.startButtonImage?.height || 'auto', maxWidth: '100%', maxHeight: size, objectFit: 'contain', opacity: screen.startButtonImage?.opacity ?? 1 }} /> :
        <div style={{ position: 'relative', isolation: 'isolate', width: '100%', padding: '7% 8% 8%', border: '2px solid transparent', borderRadius: size * .12, textAlign: 'center', color: '#fff' }}>
          {settings.backgroundEnabled && <div data-prompt-panel aria-hidden="true" style={{ position: 'absolute', inset: -2, zIndex: -1, borderRadius: 'inherit', background: '#0b1220', border: '2px solid #ffffff80', boxShadow: '0 18px 60px #0009, 0 0 0 1px #0008', opacity: settings.backgroundOpacity }} />}
          <div data-prompt-artwork style={{ height: size * .58, opacity: settings.artworkOpacity }}><StartArtwork {...settings} /></div>
          <div style={{ fontFamily: 'sans-serif', fontSize: size * .067, fontWeight: 800, lineHeight: 1.25, overflowWrap: 'anywhere', opacity: settings.textOpacity, textShadow: '0 2px 5px #000, 0 0 2px #000' }}><span data-prompt-text>{settings.label || START_PROMPT_DEFAULTS.label}</span></div>
          <div data-prompt-accent style={{ width: '18%', height: 4, borderRadius: 4, background: settings.accent, margin: '7% auto 0', opacity: settings.artworkOpacity }} />
        </div>}
    </PromptMotion>
  </div>
}
